/**
 * Canonical model catalogue. Single source of truth for the inspector AI editor
 * (model dropdown + thinking selector) and the route handler validation.
 *
 * Reasoning landscape (verified May 2026):
 *
 * OpenAI (`reasoning_effort` parameter on the Responses / Chat Completions API):
 *   - gpt-5.5            → low | medium (default) | high | xhigh
 *   - gpt-5.2            → low | medium (default) | high | xhigh
 *   - gpt-5.1-codex-max  → low | medium (default) | high | xhigh
 *   - gpt-5.1            → low | medium (default) | high
 *   - gpt-5, gpt-5-mini, gpt-5-nano → minimal | low | medium (default) | high
 *   - o-series (o4-mini, o3, o1) → low | medium (default) | high
 *   - gpt-4o, gpt-4o-mini → no reasoning_effort support
 *
 * Anthropic (`thinking` parameter on the Messages API):
 *   - Adaptive thinking (`{ type: "adaptive" }`) — recommended on Opus 4.6+,
 *     Sonnet 4.6, Haiku 4.5+. Model decides budget dynamically.
 *   - Legacy fixed budget (`{ type: "enabled", budget_tokens: N }`) — still
 *     supported; min 1024, must be < max_tokens.
 *
 * Refs:
 *   https://platform.openai.com/docs/guides/reasoning
 *   https://platform.openai.com/docs/guides/latest-model
 *   https://platform.claude.com/docs/en/build-with-claude/adaptive-thinking
 *   https://platform.claude.com/docs/en/build-with-claude/extended-thinking
 */

import type { AIProvider, AIReasoning } from "./types";

export type OpenAIEffort = "minimal" | "low" | "medium" | "high" | "xhigh";

/** Describes how a single model handles reasoning + which controls to render in the UI. */
export type ModelCapability = {
  /** Model id passed to the provider SDK. */
  id: string;
  /** Human-readable label for the dropdown. */
  label: string;
  /** Short tagline shown under the option (use case hint). */
  tagline?: string;
  /**
   * Shape of reasoning controls this model accepts.
   * - "openai-effort":      render a [Off | minimal | low | medium | high | xhigh] segmented control,
   *                         filtered by `effortValues`.
   * - "anthropic-thinking": render [Off | Adaptive | Fixed budget…] with an input when Fixed.
   * - "none":               hide the reasoning section.
   */
  reasoningKind: "openai-effort" | "anthropic-thinking" | "none";
  /** Only for openai-effort: which effort values this model accepts. */
  effortValues?: ReadonlyArray<OpenAIEffort>;
  /** Only for openai-effort: model's documented default. */
  defaultEffort?: OpenAIEffort;
  /** Only for anthropic-thinking: does the model support `{ type: "adaptive" }`? */
  supportsAdaptive?: boolean;
  /** Indicative input/output price band — purely informational for the UI ("cheap" / "balanced" / "premium"). */
  tier?: "cheap" | "balanced" | "premium";
  /**
   * Only for openai-effort: the model always reasons (no "off"). The UI hides
   * the Off button and new rules start at the lowest effort. OpenRouter lists
   * these as `reasoning.mandatory`.
   */
  reasoningRequired?: boolean;
};

// ---------------------------------------------------------------------------
// OpenAI
// ---------------------------------------------------------------------------

export const OPENAI_MODELS: ReadonlyArray<ModelCapability> = [
  {
    id: "gpt-5.5",
    label: "GPT-5.5",
    tagline: "Latest flagship, xhigh available",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high", "xhigh"],
    defaultEffort: "medium",
    tier: "premium",
  },
  {
    id: "gpt-5.2",
    label: "GPT-5.2",
    tagline: "Strong reasoning",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high", "xhigh"],
    defaultEffort: "medium",
    tier: "premium",
  },
  {
    id: "gpt-5.1",
    label: "GPT-5.1",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high"],
    defaultEffort: "medium",
    tier: "premium",
  },
  {
    id: "gpt-5",
    label: "GPT-5",
    reasoningKind: "openai-effort",
    effortValues: ["minimal", "low", "medium", "high"],
    defaultEffort: "medium",
    tier: "premium",
  },
  {
    id: "gpt-5-mini",
    label: "GPT-5 mini",
    tagline: "Smaller, cheaper, still reasons",
    reasoningKind: "openai-effort",
    effortValues: ["minimal", "low", "medium", "high"],
    defaultEffort: "medium",
    tier: "balanced",
  },
  {
    id: "gpt-5-nano",
    label: "GPT-5 nano",
    tagline: "Cheapest reasoning model",
    reasoningKind: "openai-effort",
    effortValues: ["minimal", "low", "medium", "high"],
    defaultEffort: "minimal",
    tier: "cheap",
  },
  {
    id: "o4-mini",
    label: "o4-mini",
    tagline: "Compact reasoning",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high"],
    defaultEffort: "medium",
    tier: "balanced",
  },
  {
    id: "gpt-4o",
    label: "GPT-4o",
    tagline: "Fast, no reasoning",
    reasoningKind: "none",
    tier: "balanced",
  },
  {
    id: "gpt-4o-mini",
    label: "GPT-4o mini",
    tagline: "Cheap baseline",
    reasoningKind: "none",
    tier: "cheap",
  },
];

// ---------------------------------------------------------------------------
// Anthropic
// ---------------------------------------------------------------------------

export const ANTHROPIC_MODELS: ReadonlyArray<ModelCapability> = [
  {
    id: "claude-opus-4-8",
    label: "Claude Opus 4.8",
    tagline: "Highest capability",
    reasoningKind: "anthropic-thinking",
    supportsAdaptive: true,
    tier: "premium",
  },
  {
    id: "claude-opus-4-7",
    label: "Claude Opus 4.7",
    tagline: "Top-tier reasoning",
    reasoningKind: "anthropic-thinking",
    supportsAdaptive: true,
    tier: "premium",
  },
  {
    id: "claude-sonnet-4-6",
    label: "Claude Sonnet 4.6",
    tagline: "Balanced default",
    reasoningKind: "anthropic-thinking",
    supportsAdaptive: true,
    tier: "balanced",
  },
  {
    id: "claude-haiku-4-5-20251001",
    label: "Claude Haiku 4.5",
    tagline: "Cheap + fast",
    reasoningKind: "anthropic-thinking",
    supportsAdaptive: true,
    tier: "cheap",
  },
  {
    id: "claude-opus-4-6",
    label: "Claude Opus 4.6",
    tagline: "Legacy Opus",
    reasoningKind: "anthropic-thinking",
    supportsAdaptive: true,
    tier: "premium",
  },
];

// ---------------------------------------------------------------------------
// Claude via the local Claude Code CLI ("claude-local") — billed against the
// user's Claude subscription (Pro/Max). Only offered when the backend runs on
// the user's own machine with AI_LOCAL_CLI=true.
//
// Ids are passed straight to `claude -p --model <id>`. The CLI accepts the
// aliases "sonnet" / "opus" / "haiku" (latest model of that family) as well as
// full model names; "default" omits --model so the CLI's own default is used.
// Reasoning controls are not plumbed through the CLI.
// ---------------------------------------------------------------------------

export const CLAUDE_LOCAL_MODELS: ReadonlyArray<ModelCapability> = [
  {
    id: "sonnet",
    label: "Sonnet (latest)",
    tagline: "Balanced default",
    reasoningKind: "none",
    tier: "balanced",
  },
  {
    id: "opus",
    label: "Opus (latest)",
    tagline: "Highest quality, uses more of your limits",
    reasoningKind: "none",
    tier: "premium",
  },
  {
    id: "haiku",
    label: "Haiku (latest)",
    tagline: "Fast, light on limits",
    reasoningKind: "none",
    tier: "cheap",
  },
  {
    id: "default",
    label: "Claude Code default",
    tagline: "Whatever your CLI is set to",
    reasoningKind: "none",
    tier: "balanced",
  },
  {
    id: "claude-opus-5",
    label: "Claude Opus 5",
    reasoningKind: "none",
    tier: "premium",
  },
  {
    id: "claude-sonnet-5",
    label: "Claude Sonnet 5",
    reasoningKind: "none",
    tier: "balanced",
  },
  {
    id: "claude-opus-4-8",
    label: "Claude Opus 4.8",
    reasoningKind: "none",
    tier: "premium",
  },
  {
    id: "claude-sonnet-4-6",
    label: "Claude Sonnet 4.6",
    reasoningKind: "none",
    tier: "balanced",
  },
  {
    id: "claude-haiku-4-5",
    label: "Claude Haiku 4.5",
    reasoningKind: "none",
    tier: "cheap",
  },
];

// ---------------------------------------------------------------------------
// OpenRouter — one key, many vendors (verified against openrouter.ai/api/v1/models,
// Sep 2026). Chat models go through the OpenAI-compatible
// /api/v1/chat/completions; reasoning uses OpenRouter's unified
// `reasoning: { effort }` (off = `effort: "none"`). Ids starting with "~" are
// OpenRouter aliases that track the vendor's newest release.
// ---------------------------------------------------------------------------

export const OPENROUTER_MODELS: ReadonlyArray<ModelCapability> = [
  {
    id: "~deepseek/deepseek-flash-latest",
    label: "DeepSeek Flash (latest)",
    tagline: "Cheapest good writer, great default",
    reasoningKind: "openai-effort",
    effortValues: ["low", "high"],
    tier: "cheap",
  },
  {
    id: "deepseek/deepseek-v4.1-flash",
    label: "DeepSeek V4.1 Flash",
    tagline: "Pinned version of Flash",
    reasoningKind: "openai-effort",
    effortValues: ["low", "high"],
    tier: "cheap",
  },
  {
    id: "~deepseek/deepseek-pro-latest",
    label: "DeepSeek Pro (latest)",
    tagline: "Stronger DeepSeek, still cheap",
    reasoningKind: "openai-effort",
    effortValues: ["low", "high"],
    tier: "balanced",
  },
  {
    id: "qwen/qwen3.8-flash",
    label: "Qwen3.8 Flash",
    tagline: "Very cheap, fast",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high"],
    tier: "cheap",
  },
  {
    id: "z-ai/glm-5.3-flash",
    label: "GLM 5.3 Flash",
    tagline: "Cheap, always reasons",
    reasoningKind: "openai-effort",
    effortValues: ["low", "high"],
    reasoningRequired: true,
    tier: "cheap",
  },
  {
    id: "google/gemini-3.5-flash-lite",
    label: "Gemini 3.5 Flash Lite",
    tagline: "Cheap Google model",
    reasoningKind: "openai-effort",
    effortValues: ["minimal", "low", "medium", "high"],
    reasoningRequired: true,
    tier: "cheap",
  },
  {
    id: "google/gemini-3.8-flash",
    label: "Gemini 3.8 Flash",
    tagline: "Balanced Google model",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high"],
    reasoningRequired: true,
    tier: "balanced",
  },
  {
    id: "openai/gpt-5.6-luna",
    label: "GPT-5.6 Luna",
    tagline: "Cheap OpenAI model",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "high", "xhigh"],
    tier: "cheap",
  },
  {
    id: "moonshotai/kimi-k3",
    label: "Kimi K3",
    tagline: "Strong long-form writing",
    reasoningKind: "openai-effort",
    effortValues: ["low", "high"],
    tier: "premium",
  },
  {
    id: "typesafe/jev-router",
    label: "Jev Router (auto-pick)",
    tagline: "Jev picks the model per request for cost vs quality",
    reasoningKind: "none",
    tier: "balanced",
  },
  {
    id: "qwen/qwen3.8-27b:free",
    label: "Qwen3.8 27B (free)",
    tagline: "Free, rate-limited",
    reasoningKind: "openai-effort",
    effortValues: ["low", "medium", "xhigh"],
    tier: "cheap",
  },
];

/**
 * Jev — TypeSafe's "System One" decision model on OpenRouter. Not a chat model:
 * it answers typed questions (choice / yes-no) with probabilities through
 * POST /api/alpha/decisions and bills input tokens only. Used by AI rules
 * that carry a `decision`, never by the free-text model dropdown.
 */
export const JEV_DECISION_MODELS: ReadonlyArray<ModelCapability> = [
  {
    id: "typesafe/jev-1.13",
    label: "Jev 1.13",
    tagline: "Pinned: thresholds stay stable",
    reasoningKind: "none",
    tier: "cheap",
  },
  {
    id: "~typesafe/jev-latest",
    label: "Jev (latest)",
    tagline: "Tracks the newest Jev release",
    reasoningKind: "none",
    tier: "cheap",
  },
];

export const DEFAULT_JEV_MODEL = "typesafe/jev-1.13";

/** True for a Jev decision-model id (`typesafe/jev-1.13`, `~typesafe/jev-latest`, …). */
export function isJevDecisionModel(modelId: string): boolean {
  return /^~?typesafe\/jev-(?!router)/.test(modelId);
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export const MODELS_BY_PROVIDER: Record<AIProvider, ReadonlyArray<ModelCapability>> = {
  openai: OPENAI_MODELS,
  anthropic: ANTHROPIC_MODELS,
  "claude-local": CLAUDE_LOCAL_MODELS,
  openrouter: OPENROUTER_MODELS,
};

/** True for the provider that runs the local Claude Code CLI (no API key). */
export function isLocalProvider(provider: AIProvider): boolean {
  return provider === "claude-local";
}

/** Find a model by id within a provider's list. Returns undefined for custom ids. */
export function modelById(
  provider: AIProvider,
  id: string,
): ModelCapability | undefined {
  return MODELS_BY_PROVIDER[provider]?.find((m) => m.id === id);
}

/**
 * Suggest a sensible default `reasoning` for a model. Used when the UI swaps
 * the model so we don't leave a stale `effort` that the new model rejects.
 */
export function defaultReasoningFor(
  provider: AIProvider,
  modelId: string,
): AIReasoning {
  const m = modelById(provider, modelId);
  if (!m || m.reasoningKind === "none") return { kind: "off" };
  if (m.reasoningKind === "openai-effort") {
    // OpenRouter bills reasoning as output tokens, so its models start with
    // thinking off (or at the lowest effort when the model can't turn it off).
    if (provider === "openrouter") {
      return m.reasoningRequired
        ? { kind: "openai-effort", effort: m.effortValues?.[0] ?? "low" }
        : { kind: "off" };
    }
    return { kind: "openai-effort", effort: m.defaultEffort ?? "medium" };
  }
  if (m.reasoningKind === "anthropic-thinking") {
    return m.supportsAdaptive ? { kind: "anthropic-adaptive" } : { kind: "off" };
  }
  return { kind: "off" };
}

/**
 * Validate that a `reasoning` shape is compatible with a model. Returns true
 * iff the route can forward the param to the provider without an error.
 *
 * Custom model ids (not in the catalogue) get a permissive pass — we don't
 * want to block users from trying brand-new models the catalogue hasn't
 * caught up to yet.
 */
export function isReasoningCompatible(
  provider: AIProvider,
  modelId: string,
  reasoning: AIReasoning,
): boolean {
  if (reasoning.kind === "off") return true;
  const m = modelById(provider, modelId);
  if (!m) return true; // unknown / custom model — permissive
  if (m.reasoningKind === "openai-effort") {
    return (
      reasoning.kind === "openai-effort" &&
      (m.effortValues?.includes(reasoning.effort) ?? false)
    );
  }
  if (m.reasoningKind === "anthropic-thinking") {
    if (reasoning.kind === "anthropic-adaptive") return !!m.supportsAdaptive;
    if (reasoning.kind === "anthropic-budget") {
      return reasoning.budgetTokens >= 1024;
    }
    return false;
  }
  return false; // model is "none" but reasoning isn't "off"
}
